import { buildAuthUrl, fetchWithAccessToken, fetchWithToken } from '../services/HttpService';
import { setAccessToken, setRefreshToken, setTenantSelectionPending, setAvailableTenants } from './tokenUtils';
import { clearUIState } from './uiStateUtils';
import { queryClient } from './queryClient';

/**
 * Fetches the list of tenants the current user has access to from BadDog.
 * Requires a valid BADDOG access token (call after authhandoff).
 * @returns {Promise<Array<{tenantname: string}>>}
 */
export const fetchAvailableTenants = async () => {
    const url = buildAuthUrl('auth/mytenants');
    const response = await fetchWithAccessToken(url);
    if (!response.ok) {
        throw new Error(`Failed to fetch available tenants: ${response.status}`);
    }
    return response.json();
};

/**
 * Switches the current session to a different Telescope tenant.
 * Silently acquires a fresh Entra ID token, exchanges it at BadDog for tenant-scoped
 * BADDOG tokens, then clears stale query cache and UI state.
 *
 * @param {import('@azure/msal-browser').PublicClientApplication} msalInstance
 * @param {string} tenantName  - The tenantname value from /auth/mytenants
 * @returns {Promise<boolean>} true on success, false on failure
 */
export const performTenantSwitch = async (msalInstance, tenantName) => {
    try {
        const account = msalInstance.getActiveAccount();
        if (!account) throw new Error('No active MSAL account');

        // Silently acquire a fresh Entra ID token (no user interaction required)
        const tokenResponse = await msalInstance.acquireTokenSilent({
            account,
            scopes: ['openid', 'profile', 'email'],
        });

        const idToken = tokenResponse.idToken;
        if (!idToken) throw new Error('acquireTokenSilent returned no ID token');

        // Exchange the fresh Entra ID token for tenant-scoped BADDOG tokens
        const url = buildAuthUrl('auth/authhandoff', idToken, tenantName);
        const response = await fetchWithToken(url);

        if (!response.ok) {
            console.error('Tenant switch authhandoff failed:', response.status);
            return false;
        }

        const data = await response.json();
        setAccessToken(data.access_token);
        setRefreshToken(data.refresh_token);
        setTenantSelectionPending(false);

        // Clear stale data from the previous tenant
        queryClient.clear();
        clearUIState();

        return true;
    } catch (error) {
        console.error('performTenantSwitch error:', error);
        return false;
    }
};
